import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
// Despite the name, this REMOVES Pixi's need for `new Function()`: it swaps
// the shader and uniform systems for versions that build their code without
// evaluating strings. Without it a strict Content-Security-Policy kills the
// renderer at init with "Current environment does not allow unsafe-eval", and
// every board stays blank while the HTML around it — buttons, balance, sound
// — keeps working, so nothing looks obviously broken.
//
// Not reproducible in development: the Vite dev server sends no CSP at all,
// and only packages/client/server.mjs does. A side-effect import, and it has
// to run before any Application is constructed — hence here rather than in
// PixiSlot/PixiTumble, which are two entry points into the same problem.
import 'pixi.js/unsafe-eval'

import { App } from './App'
import { installGlobalErrorHandlers } from './log'
import './styles.css'

// Before anything else: a crash during boot is the one we are least likely
// to hear about by any other route.
installGlobalErrorHandlers()

const host = document.getElementById('root')
if (!host) throw new Error('No #root element in index.html')

/**
 * Pixi rasterises every Text into a WebGL texture once, at the moment the
 * scene is built. If the web fonts are still in flight then, the reels bake
 * in the fallback and keep it for the session, while the surrounding HTML
 * swaps to the real face — so the board and the chrome end up in different
 * typefaces with no error anywhere.
 *
 * Waiting for `document.fonts.ready` closes that window. The timeout is the
 * safety valve: a slow or unreachable font CDN must delay the boot, never
 * block it.
 */
const FONT_TIMEOUT_MS = 2000

async function fontsSettled(): Promise<void> {
  if (!document.fonts) return
  await Promise.race([
    document.fonts.ready,
    new Promise((resolve) => setTimeout(resolve, FONT_TIMEOUT_MS)),
  ])
}

void fontsSettled().then(() => {
  createRoot(host).render(
    <StrictMode>
      <App />
    </StrictMode>,
  )
})
